import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {
  IcoApple,
  IcoCalendar,
  IcoDownload,
  IcoGoogle,
  IcoOutlook,
  IcoYahoo,
} from '@components/icons';
import { toISO8601 } from 'shared/dateutil';
import { toQueryString } from 'shared/urls';
import { Dropdown, MenuItem } from '@components/dropdown';

dayjs.extend(utc);

type Props = {
  title: string;
  startDate: string | Date;
  durationMinutes: number;
  description?: string;
  recurrence?: string;
  location?: string;
};

function buildGoogleLink({
  title,
  startDate,
  durationMinutes,
  description,
  recurrence,
  location,
}: Props) {
  const start = dayjs(startDate);
  const end = start.add(durationMinutes, 'minutes');
  const params = toQueryString({
    text: title,
    details: description || '',
    dates: `${toISO8601(start.toDate())}/${toISO8601(end.toDate())}`,
    ...(recurrence ? { recur: recurrence } : {}),
    ...(location ? { location } : {}),
    sf: true,
  });
  return `https://www.google.com/calendar/render?action=TEMPLATE&${params}`;
}

function buildOutlookLink(props: Props, host: string) {
  const start = dayjs(props.startDate);
  const end = start.add(props.durationMinutes, 'minutes');
  const params = toQueryString({
    rru: 'addevent',
    subject: props.title,
    body: props.description || '',
    startdt: start.toISOString(),
    enddt: end.toISOString(),
    ...(props.location ? { location: props.location } : {}),
    path: '/calendar/action/compose',
  });
  return `https://${host}/calendar/0/action/compose?${params}`;
}

function buildYahooLink({ title, startDate, durationMinutes, description, location }: Props) {
  const start = dayjs(startDate);
  const hours = Math.floor(durationMinutes / 60);
  const mins = durationMinutes % 60;
  const dur = `${String(hours).padStart(2, '0')}${String(mins).padStart(2, '0')}`;
  const params = toQueryString({
    v: 60,
    title,
    desc: description || '',
    st: toISO8601(start.toDate()),
    dur,
    ...(location ? { in_loc: location } : {}),
  });
  return `https://calendar.yahoo.com/?${params}`;
}

function escapeIcs(text: string): string {
  return text
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n');
}

function buildIcsBlob({
  title,
  startDate,
  durationMinutes,
  description,
  recurrence,
  location,
}: Props): string {
  const start = dayjs(startDate);
  const dtstamp = toISO8601(new Date());
  const dtstart = toISO8601(start.toDate());
  const hours = Math.floor(durationMinutes / 60);
  const mins = durationMinutes % 60;
  return (
    [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Mirasee//Event//EN',
      'BEGIN:VEVENT',
      `UID:mirasee-${Date.now()}-${Math.random().toString(36).slice(2, 10)}@mirasee.com`,
      `DTSTAMP:${dtstamp}`,
      `DTSTART:${dtstart}`,
      `DURATION:PT${hours}H${mins}M`,
      recurrence ? recurrence : '',
      `SUMMARY:${escapeIcs(title)}`,
      description ? `DESCRIPTION:${escapeIcs(description)}` : '',
      location ? `LOCATION:${escapeIcs(location)}` : '',
      'END:VEVENT',
      'END:VCALENDAR',
    ]
      .filter(Boolean)
      .join('\r\n') + '\r\n'
  );
}

function downloadIcs(props: Props) {
  const content = buildIcsBlob(props);
  const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${props.title}.ics`;
  a.click();
  URL.revokeObjectURL(url);
}

export function AddToCalendarDropdown(props: Props) {
  const googleLink = buildGoogleLink(props);
  const outlookLiveLink = buildOutlookLink(props, 'outlook.live.com');
  const office365Link = buildOutlookLink(props, 'outlook.office.com');
  const yahooLink = buildYahooLink(props);

  return (
    <div class="flex justify-center my-6">
      <Dropdown
        triggerClass="border-gray-300 hover:bg-gray-50 btn gap-2"
        renderMenu={() => (
          <div class="flex flex-col">
            <MenuItem onClick={() => downloadIcs(props)} regularCase>
              <IcoApple class="h-4 w-4 opacity-75" />
              Apple Calendar
            </MenuItem>
            <MenuItem href={googleLink} target="_blank" rel="noopener noreferrer" regularCase>
              <IcoGoogle class="h-4 w-4 opacity-75" />
              Google Calendar
            </MenuItem>
            <MenuItem href={outlookLiveLink} target="_blank" rel="noopener noreferrer" regularCase>
              <IcoOutlook class="h-4 w-4 opacity-75" />
              Outlook.com
            </MenuItem>
            <MenuItem href={office365Link} target="_blank" rel="noopener noreferrer" regularCase>
              <IcoOutlook class="h-4 w-4 opacity-75" />
              Office 365
            </MenuItem>
            <MenuItem href={yahooLink} target="_blank" rel="noopener noreferrer" regularCase>
              <IcoYahoo class="h-4 w-4 opacity-75" />
              Yahoo Calendar
            </MenuItem>
            <MenuItem onClick={() => downloadIcs(props)} regularCase>
              <IcoDownload class="h-4 w-4 opacity-75" />
              Download .ics file
            </MenuItem>
          </div>
        )}
      >
        <IcoCalendar class="w-4 h-4 opacity-75" />
        Add to Calendar
      </Dropdown>
    </div>
  );
}
